import { parseLocation, type Route } from './routes';

/**
 * A minimal history-backed location store. React subscribes to it with
 * useSyncExternalStore; there is one source of truth for the current route.
 */
type Listener = () => void;

export interface LocationSnapshot {
  readonly pathname: string;
  readonly search: string;
  readonly route: Route;
}

const listeners = new Set<Listener>();
let current: LocationSnapshot = snapshot();

function snapshot(): LocationSnapshot {
  const { pathname, search } = window.location;
  return { pathname, search, route: parseLocation(pathname) };
}

function emit(): void {
  const next = snapshot();
  if (next.pathname === current.pathname && next.search === current.search) return;
  current = next;
  listeners.forEach((listener) => {
    listener();
  });
}

window.addEventListener('popstate', emit);

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getLocation(): LocationSnapshot {
  return current;
}

export function push(url: string): void {
  window.history.pushState({}, '', url);
  emit();
}

export function replace(url: string): void {
  window.history.replaceState({}, '', url);
  emit();
}
